import { useEffect, useRef, useState } from "react";
import { MAX_CITY } from "../../src/city.ts";
import { api, type Item } from "./api.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button, Icon, TextField } from "./souvenir.ts";

// A stay's city, under its title on its card (DESIGN.md, Views: one of the few things the web changes;
// features/stays-and-moves.md): the city or town it's in, as people say it, which heads its group on the
// map's list and in the rating deck. Edit (or Add its city) turns it into a field, Enter or Save keeps it
// (empty takes it off), Escape or Cancel leaves it, the focus back on Edit; each save is my change "on the
// web", in Updates, with Undo here at once. Only a stay with a place has one.

export function StayCity({ item, trip, onChanged }: { item: Item; trip: string; onChanged: () => void }) {
  const stored = item.where?.city ?? null;
  // A read-only trip: its city to read, nothing to change.
  const readOnly = useReadOnly();
  // What I just saved, shown until the trip loads with it (its version), with the change that saved it
  // (for Undo) and what it was before; after Undo, what it was again, until the trip loads with that.
  const [saved, setSaved] = useState<{
    city: string | null;
    before: string | null;
    version: number;
    change: string | null;
  } | null>(null);
  const shown = saved && item.version < saved.version ? saved.city : stored;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(shown ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLFormElement>(null);
  const line = useRef<HTMLDivElement>(null);
  // Closing the field (saved or not) puts the focus back on Edit, where it was.
  const [refocus, setRefocus] = useState(false);
  const close = () => {
    setEditing(false);
    setRefocus(true);
  };
  useEffect(() => {
    if (!refocus || editing) return;
    setRefocus(false);
    line.current?.querySelector<HTMLElement>(".sv-btn")?.focus();
  }, [refocus, editing]);
  // Another stay opened in the same card: nothing of this one's carries over.
  // biome-ignore lint/correctness/useExhaustiveDependencies: again for each item
  useEffect(() => {
    setSaved(null);
    setEditing(false);
    setError(null);
  }, [item.id]);
  // The field takes the focus as it opens, with what's there selected, and is as long as a city's name
  // may be.
  useEffect(() => {
    if (!editing) return;
    const input = field.current?.querySelector("input");
    if (!input) return;
    input.maxLength = MAX_CITY;
    input.autocomplete = "off";
    input.focus();
    input.select();
  }, [editing]);

  const save = async () => {
    const city = text.trim() || null;
    if (city === shown) return close();
    setBusy(true);
    setError(null);
    try {
      const { change, version } = await api.setCity(trip, item.id, city);
      setSaved({ city, before: shown, version, change });
      close();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the city. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    if (!saved?.change) return;
    setBusy(true);
    setError(null);
    try {
      await api.undo(trip, saved.change);
      // As it was before, until the trip loads with the undo (a version on).
      setSaved({ city: saved.before, before: saved.city, version: saved.version + 1, change: null });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't undo. Try again from Updates.");
    } finally {
      setBusy(false);
    }
  };

  if (editing)
    return (
      <form
        ref={field}
        className="tm-city-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          // Leaving the field, before the page's own Escape (which closes the card).
          e.preventDefault();
          e.stopPropagation();
          close();
        }}
      >
        <TextField
          label="City"
          hint="The city or town it's in, as people say it. It heads this stay's places on the map's list."
          value={text}
          onChange={(e) => setText((e.target as HTMLInputElement).value)}
        />
        <span className="tm-note-actions">
          <Button variant="secondary" size="sm" type="submit" disabled={busy}>
            Save
          </Button>
          <Button variant="quiet" size="sm" type="button" onClick={close}>
            Cancel
          </Button>
        </span>
        {error && (
          <p className="caption tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
      </form>
    );
  const open = () => {
    setText(shown ?? "");
    setError(null);
    setEditing(true);
  };
  if (readOnly)
    return shown ? (
      <div className="tm-city">
        <span className="callout tm-city-name">
          <Icon name="pin" size={20} />
          <span>{shown}</span>
        </span>
      </div>
    ) : null;
  return (
    <div className="tm-city" ref={line}>
      {shown ? (
        <>
          <span className="callout tm-city-name">
            <Icon name="pin" size={20} />
            <span>{shown}</span>
          </span>
          <Button variant="quiet" size="sm" type="button" aria-label="Edit the city" data-edit="city" onClick={open}>
            Edit
          </Button>
        </>
      ) : (
        <Button
          variant="quiet"
          size="sm"
          type="button"
          icon="pin"
          className="tm-city-add"
          data-edit="city"
          onClick={open}
        >
          Add its city
        </Button>
      )}
      {saved?.change && (
        <Button variant="quiet" size="sm" type="button" icon="replay" disabled={busy} onClick={undo}>
          Undo
        </Button>
      )}
      {error && (
        <p className="caption tm-error tm-city-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}
